/* ChonkSat — orbital survey globe for the ChonkDex.
 *
 * Renders every Chonk as a blip on a spinnable CRT globe, placed in the real-world habitat
 * that fits its story. No dependencies: an orthographic projection drawn on a 2D canvas.
 *
 * Data comes from the section (see sections/dex-globe.liquid), which emits a JSON payload
 * of specimens; coastline geometry comes from assets/dex-globe-coast.js.
 *
 * Placement, in order of preference:
 *   1. a curated entry in snippets/dex-globe-places.liquid, keyed by dex number
 *   2. the biome implied by the product's `chonk.nature` metafield, offset deterministically
 *      from the biome's centre so specimens sharing a habitat never stack
 */
(function () {
  'use strict';

  /* biome key -> label shown in the dossier */
  var BIOMES = {
    volcanic: 'Volcanic Rift', river: 'Riverlands', grass: 'Grasslands', crag: 'Crag & Highland',
    concrete: 'Concrete Wilds', wild: 'Wildlands', storm: 'Storm Coast', gloom: 'Gloomlands',
    caves: 'Cavern Deeps', jungle: 'Jungle Canopy', perch: 'Sunlit Perch', glimmer: 'Glimmerfields',
    boreal: 'Boreal Pinewood', frost: 'Frost & Cosy', polar: 'Polar Ice', wastes: 'Wastelands',
    coast: 'Tidal Coast', meadow: 'Wildflower Meadow', oak: 'Ancient Oakwood', forest: 'Forest / Mosswood',
    alpine: 'Frosted Peak', reef: 'Reef & Waters', desert: 'Dunes & Wastes', transit: 'Currently Hitchhiking',
    gpeak: 'Glimmer Peak', cocoa: 'Cocoa Grove', scrub: 'Scrublands', exile: 'Lonely Shore',
    chonktek: 'Chonktek [CLASSIFIED]'
  };

  /* `chonk.nature` metafield -> biome. Natures can be compound ("Forged/Lush"), so we test
     each token; first match wins. Anything unrecognised lands in the wildlands. */
  var NATURE_BIOME = {
    scorch: 'volcanic', splash: 'reef', lush: 'forest', flint: 'crag', feral: 'wild',
    gloom: 'gloom', skitter: 'jungle', fluffy: 'frost', glimmer: 'glimmer', forged: 'chonktek'
  };

  /* where a biome sits when no curated coordinates exist */
  var BIOME_HOME = {
    volcanic: [64.9, -19], river: [-0.7, -50], grass: [-2.3, 34.8], crag: [57, -4.5],
    concrete: [40.72, -73.99], wild: [-19, 23.5], storm: [9.8, -71.5], gloom: [29.9, -91],
    caves: [-38.26, 175.1], jungle: [0.9, 114], perch: [-3.4, 37.3], glimmer: [-30.4, -56.5],
    boreal: [61.5, 105], frost: [67.8, 20.3], polar: [78, 15.6], wastes: [40.5, 70],
    coast: [-23.4, 151.9], meadow: [46.8, 8.2], oak: [51.1, -1.5], forest: [47.7, -123.5],
    alpine: [46.5, 7.9], reef: [-18.3, 147.7], desert: [32.2, -111], transit: [0, 0],
    gpeak: [27.99, 86.9], cocoa: [6.5, -5.5], scrub: [-25.3, 131], exile: [-37.1, -12.3],
    chonktek: [0, 0]
  };

  function biomeFor(nature) {
    var parts = String(nature || '').toLowerCase().split(/[^a-z]+/);
    for (var i = 0; i < parts.length; i++) if (NATURE_BIOME[parts[i]]) return NATURE_BIOME[parts[i]];
    return 'wild';
  }

  /* stable pseudo-random offset from a string, so a specimen always lands in the same spot
     and two specimens in one biome do not sit on top of each other */
  function jitter(seed, spread) {
    var h = 0;
    for (var i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
    return ((h >>> 0) % 2000 / 1000 - 1) * spread;
  }

  /* Fill in biome/lat/lon/location for specimens the curated table does not cover. */
  function placeAll(chonks, places) {
    for (var i = 0; i < chonks.length; i++) {
      var c = chonks[i], p = places[c.dex];
      if (p) {
        c.biome = p.b; c.lat = p.lat; c.lon = p.lon;
        c.loc = c.loc || p.loc;
        if (p.r) c.redacted = true;
      } else {
        c.biome = biomeFor(c.nat);
        var home = BIOME_HOME[c.biome] || BIOME_HOME.wild;
        c.lat = Math.max(-80, Math.min(80, home[0] + jitter(c.dex + 'a', 6)));
        c.lon = home[1] + jitter(c.dex + 'b', 9);
        c.loc = c.loc || BIOMES[c.biome];
      }
    }
    return chonks;
  }

  function initGlobe(root) {
    if (root.dataset.dexGlobeReady) return;
    root.dataset.dexGlobeReady = '1';

    var payload = root.querySelector('[data-dex-globe-data]');
    var data = {};
    try { data = JSON.parse(payload.textContent); } catch (e) { return; }

    var LAND = window.DEX_GLOBE_COAST || [];
    var CHONKS = placeAll(data.chonks || [], data.places || {});
    if (!CHONKS.length) return;

    /* elements are looked up by data-g inside this section, so several globes can coexist
       and the theme editor can re-render one without the others going stale */
    function q(name) { return root.querySelector('[data-g="' + name + '"]'); }
    var rafId = 0, idleTimer = 0;



    var canvas = q('globe');
    var ctx = canvas.getContext('2d');
    var W = 0, H = 0, cx = 0, cy = 0, R = 0, R0 = 0, dpr = 1;
    var DEG = Math.PI / 180;
    var rect = { left: 0, top: 0 };
    var rectDirty = true;   /* scrolling/layout shifts move the canvas without resizing it */

    function refreshRect() {
      rect = canvas.getBoundingClientRect();
      rectDirty = false;
    }
    function markRectDirty() { rectDirty = true; }

    function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }

    /* the view we open on and that RESET returns to, as a lat/lon the camera looks at */
    var HOME_LAT = 16, HOME_LON = 100;

    function resize() {
      var box = canvas.parentElement.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = Math.max(320, box.width); H = Math.max(440, box.height);
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx = W / 2; cy = H / 2; R0 = Math.min(W, H) * 0.40;
      refreshRect();
    }

    var spin = -HOME_LON * DEG, tilt = HOME_LAT * DEG;
    var zoom = 1, targetZoom = 1, maxZoom = 22;
    var targetSpin = spin, targetTilt = tilt, animating = false;
    var vel = 0.0015, dragging = false, lastX = 0, lastY = 0, idleSpin = true;
    var hoverHit = null, selected = null;   /* hoverHit is whatever hitTest returned, or null */
    var hovering = false;   /* cursor over the globe -> pause the idle spin so targets hold still */
    var TILT_MAX = 1.3;
    function clampZoom(z) { return clamp(z, 1, maxZoom); }
    /* every "send the camera somewhere" path goes through this */
    function flyTo(lat, lon, z) {
      targetSpin = -lon * DEG; targetTilt = clamp(lat * DEG, -TILT_MAX, TILT_MAX);
      targetZoom = clampZoom(z); animating = true; idleSpin = false;
    }
    var markerScreen = [], clusters = [];
    var spider = null, spiderScreen = [];   /* spiderfy fallback: fan a stubborn cluster's members out so each is tappable */
    function clearSpider() { if (spider) { spider = null; spiderScreen = []; } }
    var cosS, sinS, cosT, sinT;

    function setRot() { cosS = Math.cos(spin); sinS = Math.sin(spin); cosT = Math.cos(tilt); sinT = Math.sin(tilt); }

    function project(lat, lon) {
      var phi = lat * DEG, lam = lon * DEG, cphi = Math.cos(phi);
      var x = cphi * Math.sin(lam), y = Math.sin(phi), z = cphi * Math.cos(lam);
      var xr = x * cosS + z * sinS, zr = -x * sinS + z * cosS;
      var yr = y * cosT - zr * sinT, zr2 = y * sinT + zr * cosT;
      return { x: cx + R * xr, y: cy - R * yr, z: zr2 };
    }
    function unproject(px, py) {
      var x = (px - cx) / R, y = -(py - cy) / R, d = x * x + y * y;
      if (d > 1) return null;
      var z = Math.sqrt(1 - d);
      var y0 = y * cosT + z * sinT, zr = z * cosT - y * sinT;
      var x0 = x * cosS - zr * sinS, z0 = x * sinS + zr * cosS;
      return { lat: Math.asin(clamp(y0, -1, 1)) / DEG, lon: Math.atan2(x0, z0) / DEG };
    }

    function circle(x, y, r) { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); }

    /* add one lat/lon path to the current canvas path, breaking the line wherever it passes
       behind the horizon (moveTo starts a fresh subpath on the way back into view) */
    function tracePath(points, zMin) {
      var started = false, j, p;
      for (j = 0; j < points.length; j++) {
        p = project(points[j][0], points[j][1]);
        if (p.z < zMin) { started = false; continue; }
        if (started) ctx.lineTo(p.x, p.y); else { ctx.moveTo(p.x, p.y); started = true; }
      }
    }

    /* graticule rings are fixed geometry — build the lat/lon lists once, not every frame */
    var GRATICULE = (function () {
      var rings = [], ring, lat, lon;
      for (lat = -60; lat <= 60; lat += 30) { ring = []; for (lon = -180; lon <= 180; lon += 5) ring.push([lat, lon]); rings.push(ring); }
      for (lon = -180; lon < 180; lon += 30) { ring = []; for (lat = -90; lat <= 90; lat += 5) ring.push([lat, lon]); rings.push(ring); }
      return rings;
    })();

    function drawSphere() {
      var g = ctx.createRadialGradient(cx - R * 0.32, cy - R * 0.32, R * 0.1, cx, cy, R);
      g.addColorStop(0, 'rgba(20,70,40,0.5)'); g.addColorStop(1, 'rgba(4,17,10,0.92)');
      circle(cx, cy, R); ctx.fillStyle = g; ctx.fill();
      circle(cx, cy, R);
      ctx.strokeStyle = 'rgba(63,240,122,0.55)'; ctx.lineWidth = 1.5;
      ctx.shadowColor = 'rgba(63,240,122,0.8)'; ctx.shadowBlur = 12; ctx.stroke(); ctx.shadowBlur = 0;
    }
    function drawGraticule() {
      ctx.lineWidth = 1; ctx.strokeStyle = 'rgba(39,178,87,0.30)';
      ctx.beginPath();
      for (var i = 0; i < GRATICULE.length; i++) tracePath(GRATICULE[i], 0);
      ctx.stroke();
    }
    function drawLand() {
      ctx.lineWidth = 1.3; ctx.strokeStyle = 'rgba(90,240,140,0.9)';
      ctx.shadowColor = 'rgba(63,240,122,0.45)'; ctx.shadowBlur = 4; ctx.lineJoin = 'round';
      /* one path for all 107 polylines: a shadowed stroke is a separate blur pass each time,
         so stroking once instead of per-polyline is the difference between 107 passes and 1 */
      ctx.beginPath();
      for (var i = 0; i < LAND.length; i++) tracePath(LAND[i], 0.02);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    function buildClusters() {
      var vis = [], i;
      for (i = 0; i < markerScreen.length; i++) if (markerScreen[i].visible && !(spider && spider.indexOf(markerScreen[i].idx) >= 0)) vis.push(markerScreen[i]);
      clusters = [];
      var CR = 20, used = {};
      for (i = 0; i < vis.length; i++) {
        if (used[i]) continue;
        var gx = vis[i].x, gy = vis[i].y, members = [vis[i].idx]; used[i] = 1;
        for (var k = i + 1; k < vis.length; k++) {
          if (used[k]) continue;
          var dx = vis[k].x - gx, dy = vis[k].y - gy;
          if (dx * dx + dy * dy < CR * CR) { used[k] = 1; members.push(vis[k].idx); gx = (gx * (members.length - 1) + vis[k].x) / members.length; gy = (gy * (members.length - 1) + vis[k].y) / members.length; }
        }
        clusters.push({ x: gx, y: gy, members: members });
      }
    }

    function drawSingle(idx, x, y, t) {
      var isSel = selected === idx, isHov = hoverHit && hoverHit.type === 'marker' && hoverHit.idx === idx, active = isSel || isHov;
      var blink = 0.55 + 0.45 * Math.sin(t / 260 + idx);
      var baseR = isSel ? 5 : (isHov ? 4.5 : 3.2);
      var col = active ? '#b7ffd0' : '#3ff07a';
      var haloR = baseR * (active ? 3 : 2), haloA = active ? (0.30 + blink * 0.25) : (0.12 + blink * 0.12);
      var hg = ctx.createRadialGradient(x, y, 0, x, y, haloR);
      hg.addColorStop(0, 'rgba(63,240,122,' + haloA + ')'); hg.addColorStop(1, 'rgba(63,240,122,0)');
      ctx.beginPath(); ctx.fillStyle = hg; ctx.arc(x, y, haloR, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.fillStyle = col; ctx.shadowColor = '#3ff07a'; ctx.shadowBlur = active ? 10 : 5;
      ctx.arc(x, y, baseR, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0;
      ctx.beginPath(); ctx.strokeStyle = 'rgba(4,17,10,0.9)'; ctx.lineWidth = 1; ctx.arc(x, y, baseR, 0, Math.PI * 2); ctx.stroke();
      if (active) {
        var rr = baseR + 6 + (isSel ? Math.sin(t / 200) * 1.5 : 0);
        ctx.strokeStyle = col; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(x, y, rr, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x - rr - 3, y); ctx.lineTo(x - rr + 3, y); ctx.moveTo(x + rr - 3, y); ctx.lineTo(x + rr + 3, y);
        ctx.moveTo(x, y - rr - 3); ctx.lineTo(x, y - rr + 3); ctx.moveTo(x, y + rr - 3); ctx.lineTo(x, y + rr + 3);
        ctx.stroke();
        ctx.font = "16px 'VT323', monospace"; ctx.fillStyle = '#b7ffd0'; ctx.textAlign = 'center';
        ctx.fillText(CHONKS[idx].name.toUpperCase(), x, y - rr - 6);
      } else {
        /* faint locator ring so a lone blip reads as a target, not a stray dot */
        ctx.beginPath(); ctx.strokeStyle = 'rgba(63,240,122,' + (0.28 + blink * 0.30) + ')';
        ctx.lineWidth = 1; ctx.arc(x, y, baseR + 4, 0, Math.PI * 2); ctx.stroke();
      }
    }
    function clusterR(n) { return 9 + Math.min(n, 9); }
    function drawCluster(cl, ci, t) {
      var n = cl.members.length, active = hoverHit && hoverHit.type === 'cluster' && hoverHit.ci === ci, r = clusterR(n);
      ctx.beginPath(); ctx.fillStyle = 'rgba(6,24,13,0.82)'; ctx.arc(cl.x, cl.y, r, 0, Math.PI * 2); ctx.fill();
      ctx.lineWidth = 1.5; ctx.strokeStyle = active ? '#b7ffd0' : '#3ff07a';
      ctx.shadowColor = '#3ff07a'; ctx.shadowBlur = active ? 12 : 6;
      ctx.beginPath(); ctx.arc(cl.x, cl.y, r, 0, Math.PI * 2); ctx.stroke();
      /* faint outer pulse ring */
      var pr = r + 4 + (0.5 + 0.5 * Math.sin(t / 300 + ci)) * 3;
      ctx.globalAlpha = 0.35; ctx.beginPath(); ctx.arc(cl.x, cl.y, pr, 0, Math.PI * 2); ctx.stroke(); ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      ctx.font = "18px 'VT323', monospace"; ctx.fillStyle = active ? '#b7ffd0' : '#3ff07a';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(String(n), cl.x, cl.y + 1); ctx.textBaseline = 'alphabetic';
    }

    function drawMarkers(t) {
      markerScreen = [];
      for (var i = 0; i < CHONKS.length; i++) {
        var p = project(CHONKS[i].lat, CHONKS[i].lon);
        markerScreen.push({ idx: i, x: p.x, y: p.y, visible: p.z > 0 });
      }
      buildClusters();
      for (var c = 0; c < clusters.length; c++) {
        if (clusters[c].members.length > 1) drawCluster(clusters[c], c, t);
        else drawSingle(clusters[c].members[0], clusters[c].x, clusters[c].y, t);
      }
      drawSpider(t);
    }

    function drawSpider(t) {
      spiderScreen = [];
      if (!spider) return;
      var pts = [], i;
      for (i = 0; i < spider.length; i++) { var m = markerScreen[spider[i]]; if (m && m.visible) pts.push(spider[i]); }
      if (!pts.length) { clearSpider(); return; }
      var ax = 0, ay = 0;
      for (i = 0; i < pts.length; i++) { ax += markerScreen[pts[i]].x; ay += markerScreen[pts[i]].y; }
      ax /= pts.length; ay /= pts.length;
      var SR = 30 + pts.length * 6;   /* fan radius grows with count */
      /* connector lines + anchor */
      ctx.strokeStyle = 'rgba(63,240,122,0.45)'; ctx.lineWidth = 1;
      for (i = 0; i < pts.length; i++) {
        var ang = -Math.PI / 2 + i * (2 * Math.PI / pts.length);
        var sx = ax + Math.cos(ang) * SR, sy = ay + Math.sin(ang) * SR;
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(sx, sy); ctx.stroke();
        spiderScreen.push({ idx: pts[i], x: sx, y: sy });
      }
      ctx.beginPath(); ctx.fillStyle = 'rgba(63,240,122,0.6)'; ctx.arc(ax, ay, 2, 0, Math.PI * 2); ctx.fill();
      for (i = 0; i < spiderScreen.length; i++) drawSingle(spiderScreen[i].idx, spiderScreen[i].x, spiderScreen[i].y, t);
    }

    var startT = performance.now();
    function frame(now) {
      var t = now - startT;
      zoom += (targetZoom - zoom) * 0.16;
      if (animating) {
        var d = targetSpin - spin; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI;
        spin += d * 0.16; tilt += (targetTilt - tilt) * 0.16;
        if (Math.abs(d) < 0.002 && Math.abs(targetTilt - tilt) < 0.002 && Math.abs(targetZoom - zoom) < 0.01) animating = false;
      } else if (idleSpin && !dragging && !hovering && zoom < 1.05) { spin += vel; }
      R = R0 * zoom;
      setRot();
      ctx.clearRect(0, 0, W, H);
      drawSphere(); drawGraticule(); drawLand(); drawMarkers(t);
      updateHud();
      rafId = requestAnimationFrame(frame);
    }
    var hudLon = q('hudLon'), hudLat = q('hudLat'), hudZ = q('hudZ');
    function setText(el, s) { if (el.textContent !== s) el.textContent = s; }   /* runs every frame */
    function updateHud() {
      var lon = -((spin * 180 / Math.PI) % 360);
      while (lon > 180) lon -= 360; while (lon < -180) lon += 360;
      setText(hudLon, (lon >= 0 ? '+' : '') + lon.toFixed(1));
      setText(hudLat, (tilt >= 0 ? '+' : '') + (tilt * 180 / Math.PI).toFixed(1));
      setText(hudZ, zoom.toFixed(1) + '×');
    }

    /* ---- interaction ---- */
    /* clientX/Y are viewport-relative, so the cached canvas offset must be refreshed after
       any page/ancestor scroll. We mark it dirty on scroll and read layout only on the next
       pointer event, avoiding a forced layout on every pointermove. */
    function pt(e) {
      if (rectDirty) refreshRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
    function hitTest(px, py, minimumRadius) {
      var best = null, bestD = 1e9, i, dx, dy, d;
      minimumRadius = minimumRadius || 0;
      /* fanned spider markers take priority */
      for (i = 0; i < spiderScreen.length; i++) {
        dx = spiderScreen[i].x - px; dy = spiderScreen[i].y - py; d = dx * dx + dy * dy;
        var spiderRadius = Math.max(16, minimumRadius);
        if (d < spiderRadius * spiderRadius && d < bestD) { bestD = d; best = { type: 'marker', idx: spiderScreen[i].idx }; }
      }
      if (best) return best;
      for (var ci = 0; ci < clusters.length; ci++) {
        var cl = clusters[ci], multi = cl.members.length > 1;
        var rad = multi ? clusterR(cl.members.length) + 5 : 20;
        rad = Math.max(rad, minimumRadius);   /* touch/pen: at least a 48px target */
        var dx = cl.x - px, dy = cl.y - py, d = dx * dx + dy * dy;
        if (d < rad * rad && d < bestD) { bestD = d; best = multi ? { type: 'cluster', ci: ci } : { type: 'marker', idx: cl.members[0] }; }
      }
      return best;
    }
    function hitRadiusForPointer(e) { return e.pointerType === 'mouse' ? 0 : 24; }
    function setHover(h) {
      hoverHit = h;
      var c = h ? 'pointer' : (dragging ? 'grabbing' : 'grab');
      if (canvas.style.cursor !== c) canvas.style.cursor = c;   /* fires on every pointermove */
    }
    function scheduleIdle(ms) { clearTimeout(idleTimer); idleTimer = setTimeout(function () { if (zoom < 1.05 && !dragging) idleSpin = true; }, ms); }

    function zoomToCluster(ci) {
      var m = clusters[ci].members, la = 0, lo = 0;
      for (var i = 0; i < m.length; i++) { la += CHONKS[m[i]].lat; lo += CHONKS[m[i]].lon; }
      flyTo(la / m.length, lo / m.length, zoom * 2.3);
    }
    function zoomToPoint(px, py, factor) {
      var u = unproject(px, py); if (!u) return;
      flyTo(u.lat, u.lon, zoom * factor);
    }
    function spiderfy(members) { spider = members.slice(); animating = false; idleSpin = false; }
    /* true when a cluster's members sit so close together that zooming would never split
       them (e.g. Poko's 6 colour variants share a spot) - fan them out on the first click. */
    function clusterStacked(members) {
      if (members.length < 2) return false;
      var minLa = 1e9, maxLa = -1e9, minLo = 1e9, maxLo = -1e9, i, la, lo;
      for (i = 0; i < members.length; i++) {
        la = CHONKS[members[i]].lat; lo = CHONKS[members[i]].lon;
        if (la < minLa) minLa = la; if (la > maxLa) maxLa = la;
        if (lo < minLo) minLo = lo; if (lo > maxLo) maxLo = lo;
      }
      return Math.max(maxLa - minLa, maxLo - minLo) < 0.6;   /* degrees; ~needs >10x zoom to separate */
    }

    var DRAG_THRESHOLD = 11;
    var pointers = new Map(), downP = null, downHit = null, moved = 0, dragStarted = false;
    var pinchD0 = 0, pinchZ0 = 1, pinching = false;

    canvas.addEventListener('pointerdown', function (e) {
      canvas.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 1) {
        refreshRect();
        dragging = true; dragStarted = false; idleSpin = false; animating = false;
        var p = pt(e); lastX = p.x; lastY = p.y; downP = p; moved = 0;
        downHit = hitTest(p.x, p.y, hitRadiusForPointer(e));
      } else if (pointers.size === 2) {
        dragging = false; dragStarted = false; downHit = null; pinching = true;
        var a = Array.from(pointers.values()); pinchD0 = Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y) || 1; pinchZ0 = targetZoom;
      }
    });
    canvas.addEventListener('pointermove', function (e) {
      if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinching && pointers.size >= 2) {
        var a = Array.from(pointers.values()), d = Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y);
        targetZoom = clampZoom(pinchZ0 * d / pinchD0); if (targetZoom > 1.05) idleSpin = false; return;
      }
      var p = pt(e);
      if (dragging) {
        var dx = p.x - lastX, dy = p.y - lastY; moved += Math.abs(dx) + Math.abs(dy);
        if (!dragStarted) {
          lastX = p.x; lastY = p.y;
          if (moved < DRAG_THRESHOLD) return;
          dragStarted = true; downHit = null; clearSpider();
          return;   /* start rotation with the next delta, so crossing the threshold never jumps */
        }
        spin += dx * 0.006 / zoom; tilt = clamp(tilt + dy * 0.004 / zoom, -TILT_MAX, TILT_MAX);
        lastX = p.x; lastY = p.y; animating = false;
      } else setHover(hitTest(p.x, p.y));
    });
    /* Hold the globe still while the cursor is over it, so markers don't drift away as you
       aim - the #1 reason clicks missed. Resumes the idle spin shortly after leaving. */
    canvas.addEventListener('pointerenter', function (e) {
      hovering = true; refreshRect();
      var p = pt(e); setHover(hitTest(p.x, p.y));
    });
    canvas.addEventListener('pointerleave', function () { hovering = false; setHover(null); scheduleIdle(500); });
    function up(e) {
      var wasClick = dragging && !dragStarted && moved < DRAG_THRESHOLD && pointers.size === 1;
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinching = false;
      if (pointers.size === 0) {
        if (wasClick && downP) {
          var h = downHit || hitTest(downP.x, downP.y, hitRadiusForPointer(e));
          if (h) {
            if (h.type === 'cluster') {
              if (clusterStacked(clusters[h.ci].members) || zoom >= maxZoom - 0.2) spiderfy(clusters[h.ci].members);  /* stacked or can't zoom further -> fan out */
              else { clearSpider(); zoomToCluster(h.ci); }
            } else { clearSpider(); selectChonk(h.idx); }
          } else clearSpider();   /* tap empty space closes a fan */
        }
        dragging = false; dragStarted = false; downHit = null; scheduleIdle(2600);
      }
    }
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', function (e) {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinching = false;
      if (pointers.size === 0) { dragging = false; dragStarted = false; downHit = null; }
    });
    canvas.addEventListener('dblclick', function (e) { var p = pt(e); zoomToPoint(p.x, p.y, 1.9); });
    canvas.addEventListener('wheel', function (e) {
      e.preventDefault(); clearSpider();
      var f = Math.exp(-e.deltaY * 0.0015);
      targetZoom = clampZoom(targetZoom * f);
      if (targetZoom > 1.05) idleSpin = false; else scheduleIdle(1200);
    }, { passive: false });

    q('zIn').addEventListener('click', function () { clearSpider(); targetZoom = clampZoom(targetZoom * 1.6); idleSpin = false; });
    q('zOut').addEventListener('click', function () { clearSpider(); targetZoom = clampZoom(targetZoom / 1.6); if (targetZoom <= 1.05) scheduleIdle(700); });

    /* Mobile 'back to map': the dossier replaces the globe on phones, so this clears the
       selection and returns to the globe. (No effect on desktop, where both are visible.) */
    (function () {
      var back = q('back');
      if (!back) return;
      back.addEventListener('click', function () {
        selected = null;
        q('card').classList.remove('show');
        q('empty').style.display = '';
        var b = q('card').closest('.dexg-body'); if (b) b.classList.remove('has-specimen');
      });
    })();

    /* dossier fields that are a straight id -> property copy */
    var FIELDS = { cDex: 'dex', cRar: 'rar', cName: 'name', cFam: 'fam', cVibe: 'vibe',
                   cNat: 'nat', cLoc: 'loc', cNote: 'note', cPrice: 'price' };
    var SCAN_RESERVE = 18;   /* room fitPre leaves under the art for the "tap to enhance" label */
    var SCAN_MAX_H = 340;    /* desktop: cap the scan so the whole dossier sits on-screen without scrolling */
    /* Mobile stacks scan over text and the page scrolls freely, so drop the height cap and let
       the art fill the full column width. Desktop keeps the cap so nothing overflows the terminal. */
    function scanCap() { return window.matchMedia('(max-width: 760px)').matches ? 0 : SCAN_MAX_H; }

    function selectChonk(idx) {
      selected = idx; var c = CHONKS[idx], id;
      q('empty').style.display = 'none';
      q('card').classList.add('show');
      /* widen the dossier (and narrow the map) once there's a specimen to show */
      var bodyEl = q('card').closest('.dexg-body'); if (bodyEl) bodyEl.classList.add('has-specimen');
      for (id in FIELDS) q(id).textContent = c[FIELDS[id]];
      var adopt = q('cAdopt'); if (adopt) adopt.setAttribute('href', c.url || '#');   /* was never wired -> dead link */
      q('cArtTag').textContent = 'SCAN ' + c.dex;
      q('cBiomeLbl').textContent = BIOMES[c.biome] || c.biome;
      q('cNoteTag').textContent = c.redacted ? 'RECOVERED // CHONKTEK' : 'FIELD NOTE';

      var pre = q('cArt'), scanBox = q('cardScan');
      function show(txt, pending) { pre.classList.toggle('scan-empty', pending); pre.textContent = txt; fitPre(pre, scanBox, SCAN_RESERVE, scanCap()); }
      scanBox.toggleAttribute('data-noart', !c.photo);
      if (!c.photo) { show(c.redacted ? '▓▓ SIGNAL LOST ▓▓\nscan corrupted' : '[ no visual on file ]\n[ scan pending ]', true); return; }
      scanBox.dataset.dex = c.dex;
      /* getScan calls back synchronously when the art is already cached, so the placeholder
         only ever shows for a photo we genuinely have to decode */
      show('DECODING SCAN…', true);
      getScan(c, function (s) {
        if (selected !== idx || !s) return;
        pre.classList.remove('scan-empty');
        pre.textContent = s;
        fitPre(pre, scanBox, SCAN_RESERVE, scanCap());
        revealScan(pre, s);            /* boot as static, then resolve to the real art */
      });
    }

    /* scale an ASCII <pre> to fill its box, preserving aspect ratio (uniform scale = no distortion) */
    function fitPre(pre, box, reserveBottom, maxH) {
      if (!pre || !box) return;
      if (pre.classList.contains('scan-empty')) { pre.style.transform = 'none'; box.style.height = ''; box.style.width = ''; return; }
      if (!pre.textContent) return;
      pre.style.transform = 'none';
      box.style.width = '';   /* clear any prior narrowing so we measure the full column, not a stale width */
      var natW = pre.offsetWidth, natH = pre.offsetHeight;
      if (!natW || !natH) return;
      var cs = getComputedStyle(box);
      var padTop = parseFloat(cs.paddingTop), reserve = reserveBottom || 0;
      var avail = box.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      var s = avail / natW;
      /* Fitting width alone overflows a short terminal: the art grows past the frame and
         .dexg-frame's overflow:hidden clips the ENHANCE button above it. When a height cap
         is given, take the smaller scale — and narrow the box to match, because the decoded
         photo fills this box and would otherwise no longer sit exactly over the ASCII. */
      if (maxH) {
        var room = maxH - padTop - reserve;
        if (room > 0 && natH * s > room) {
          s = room / natH;
          box.style.width = Math.round(natW * s) + 'px';
        }
      }
      pre.style.transform = 'scale(' + s + ')';
      box.style.height = (padTop + natH * s + reserve) + 'px';
    }

    /* vertical room the enhance overlay has for the scan, minus its own chrome */
    function overlayRoom() {
      var view = q('scanZoom'), wrap = q('zWrap');
      if (!view || !wrap) return 0;
      var closeEl = view.querySelector('.zclose'), chrome = parseFloat(getComputedStyle(wrap).paddingTop) || 0;
      if (closeEl) chrome += closeEl.offsetHeight + (parseFloat(getComputedStyle(closeEl).marginTop) || 0);
      return view.clientHeight - chrome - 12;
    }

    /* ---- client-side ASCII: generate from the real photo using the browser's true font cell ---- */
    var SCAN_COLS = 160, cellAspect = 0.5, cellReady = false, imgCache = {};
    function measureCell() {
      var m = document.createElement('pre');
      m.style.cssText = "font-family:'VT323',monospace;font-size:8px;line-height:5.4px;position:absolute;left:-9999px;top:0;visibility:hidden;white-space:pre;margin:0;padding:0;border:0;";
      var line = ''; for (var i = 0; i < 120; i++) line += 'X';
      m.textContent = line + '\n' + line + '\n' + line + '\n' + line + '\n' + line + '\n' + line;
      document.body.appendChild(m);
      var r = m.getBoundingClientRect();
      if (r.width && r.height) { cellAspect = (r.width / 120) / (r.height / 6); cellReady = true; }
      document.body.removeChild(m);
    }
    function buildAscii(img) {
      if (!cellReady) measureCell();
      var COLS = SCAN_COLS, ar = img.naturalHeight / img.naturalWidth, ROWS = Math.max(10, Math.round(COLS * ar * cellAspect));
      var cv = document.createElement('canvas'); cv.width = COLS; cv.height = ROWS;
      var cx = cv.getContext('2d'); cx.drawImage(img, 0, 0, COLS, ROWS);
      var d; try { d = cx.getImageData(0, 0, COLS, ROWS).data; } catch (e) { return null; }
      function px(x, y) { var i = (y * COLS + x) * 4; return [d[i], d[i + 1], d[i + 2]]; }
      var br = 0, bgg = 0, bb = 0, n = 0, x, y, a, b2;
      for (x = 0; x < COLS; x++) { a = px(x, 0); b2 = px(x, ROWS - 1); br += a[0] + b2[0]; bgg += a[1] + b2[1]; bb += a[2] + b2[2]; n += 2; }
      for (y = 0; y < ROWS; y++) { a = px(0, y); b2 = px(COLS - 1, y); br += a[0] + b2[0]; bgg += a[1] + b2[1]; bb += a[2] + b2[2]; n += 2; }
      br /= n; bgg /= n; bb /= n;
      var ramp = " .:-=+*oX#%@", N = ramp.length, FLOOR = 70, SPAN = 135, rows = [];
      for (y = 0; y < ROWS; y++) {
        var lineStr = '';
        for (x = 0; x < COLS; x++) {
          var p = px(x, y), dist = Math.sqrt((p[0] - br) * (p[0] - br) + (p[1] - bgg) * (p[1] - bgg) + (p[2] - bb) * (p[2] - bb));
          if (dist < FLOOR) { lineStr += ' '; continue; }
          var tt = (dist - FLOOR) / SPAN; tt = tt < 0 ? 0 : (tt > 1 ? 1 : tt);
          lineStr += ramp[Math.round(tt * (N - 1))];
        }
        rows.push(lineStr);   /* keep full COLS width so the pre's aspect == the image's aspect */
      }
      return rows.join('\n');
    }
    function getScan(c, cb) {   /* returns cached ASCII, or generates from c.photo */
      if (!c.photo) { cb(null); return; }
      if (c._art) { cb(c._art); return; }
      var cached = imgCache[c.dex];
      function make(img) { var s = buildAscii(img); if (s) c._art = s; cb(s); }
      if (cached && cached.complete && cached.naturalWidth) { make(cached); return; }
      var img = cached || new Image();
      imgCache[c.dex] = img;
      img.onload = function () { make(img); };
      img.onerror = function () { cb(null); };
      if (!cached) { img.crossOrigin = 'anonymous'; img.src = c.photo; }
    }

    /* Reveal a freshly-decoded scan the CRT way: boot as full static, then let each cell
       "lock in" to its final character at a random step, so the art emerges out of the snow
       and the background cells resolve to spaces (the noise deletes away). ponytail: one shared
       timer; selecting another specimen or reduced-motion just skips straight to the finished art. */
    var revealTimer = null;
    function revealScan(pre, finalText) {
      clearInterval(revealTimer);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { pre.textContent = finalText; return; }
      var grid = finalText.split('\n').map(function (l) { return l.split(''); });
      var noise = "*+=-:oX#%@", F = 12, frame = 0;
      var thr = grid.map(function (row) { return row.map(function () { return 1 + Math.floor(Math.random() * F); }); });
      function step() {
        var out = new Array(grid.length);
        for (var y = 0; y < grid.length; y++) {
          var row = grid[y], t = thr[y], s = '';
          for (var x = 0; x < row.length; x++) s += frame >= t[x] ? row[x] : noise[(Math.random() * noise.length) | 0];
          out[y] = s;
        }
        pre.textContent = out.join('\n');
        if (frame++ >= F) { clearInterval(revealTimer); pre.textContent = finalText; }
      }
      step();                          /* frame 0 = pure static */
      revealTimer = setInterval(step, 40);
    }

    /* tap the dossier scan to open the enhance viewer. The real photo then decodes
       (flickers) in over the ASCII on its own after a short beat — long enough for
       the scan to read as a scan first. The ENHANCE button just triggers it
       immediately for the impatient. */
    (function () {
      /* named `overlay`, not `zoom` — `zoom` is the globe's zoom level in the enclosing scope */
      var box = q('cardScan'), overlay = q('scanZoom');
      var zArt = q('zArt'), zWrap = q('zWrap');
      var zPhoto = q('zPhoto'), zTag = q('zTag'), zDecode = q('zDecode'), zScreen = q('zScreen');
      if (!box || !overlay) return;

      var AUTO_DECODE_MS = 1100;   /* the beat where it reads as a scan before enhancing */
      var autoTimer = null;
      function cancelAuto() { if (autoTimer) { clearTimeout(autoTimer); autoTimer = null; } }

      /* decode the real photo in over the ASCII (the flicker/reveal is CSS on .decoded/.on) */
      function decode() {
        var c = CHONKS[selected]; if (!c || !c.photo) return;
        if (zWrap.classList.contains('decoded')) return;   /* already enhanced */
        zPhoto.onload = function () { zPhoto.classList.add('on'); };
        zPhoto.src = c.photo;
        if (zPhoto.complete && zPhoto.naturalWidth) zPhoto.classList.add('on');
        zWrap.classList.add('decoded');
        zTag.textContent = 'SCAN ' + c.dex + ' // DECODED';
        zDecode.hidden = true;
      }
      function closeOverlay() { cancelAuto(); overlay.hidden = true; }

      box.addEventListener('click', function () {
        if (box.hasAttribute('data-noart') || selected == null) return;
        var c = CHONKS[selected];
        zWrap.classList.remove('decoded');
        zPhoto.classList.remove('on'); zPhoto.removeAttribute('src');  /* CSS .zphoto{display:none} hides it until decoded */
        zTag.textContent = 'SCAN ' + c.dex;
        zDecode.hidden = !c.photo;
        overlay.hidden = false;
        getScan(c, function (s) {
          zArt.textContent = s || '';
          function refit() { fitPre(zArt, zScreen, 0, overlayRoom()); }
          requestAnimationFrame(refit);
          /* re-fit once the real font is in — fonts.ready is the actual signal, where this
             used to be a 90ms guess that a slow font load would beat */
          if (document.fonts && document.fonts.ready) document.fonts.ready.then(refit);
        });
        /* auto-enhance shortly after opening, unless they close/enhance first */
        cancelAuto();
        if (c.photo) autoTimer = setTimeout(decode, AUTO_DECODE_MS);
      });
      zDecode.addEventListener('click', function (e) {
        e.stopPropagation();
        cancelAuto();
        decode();
      });
      /* tap anywhere (except the ENHANCE button, which stops propagation) closes the viewer */
      overlay.addEventListener('click', closeOverlay);
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !overlay.hidden) closeOverlay(); });
    })();

    /* refit the dossier scan when the layout width changes, and once fonts are ready (measure with the real font) */
    function onResize() {
      if (selected == null) return;
      fitPre(q('cArt'), q('cardScan'), SCAN_RESERVE, scanCap());
      if (!q('scanZoom').hidden) fitPre(q('zArt'), q('zScreen'), 0, overlayRoom());   /* e.g. rotating with it open */
    }
    window.addEventListener('resize', onResize);
    measureCell();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () {
      measureCell();   /* re-measure with the real font, then re-decode the open scan at the correct aspect */
      if (selected != null) { CHONKS[selected]._art = null; selectChonk(selected); }
    });

    if (q('boot')) setTimeout(function () { q('boot').classList.add('hide'); }, 2200);
    /* deep-link: #dex=C14 flies to a specimen and opens its dossier; #goto=C14 just flies there */
    (function () {
      var m = location.hash.match(/(dex|goto)=([A-Za-z0-9-]+)/);
      if (!m) return;
      var want = m[2].toLowerCase(), i = -1;
      for (var k = 0; k < CHONKS.length; k++) { if (CHONKS[k].dex.replace('#', '').toLowerCase() === want) { i = k; break; } }
      if (i < 0) return;
      flyTo(CHONKS[i].lat, CHONKS[i].lon, 5);
      if (m[1] === 'dex') selectChonk(i);
    })();
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', markRectDirty, true);
    /* Re-measure whenever the stage itself changes width - e.g. the dossier widening on
       select narrows the map, which window 'resize' never fires. Keeps the globe centred
       and the scan fitted through the transition. */
    var stageRO = null;
    if (window.ResizeObserver) {
      stageRO = new ResizeObserver(function () { resize(); onResize(); });
      stageRO.observe(canvas.parentElement);
    }
    resize(); rafId = requestAnimationFrame(frame);

    root.dexGlobeDestroy = function () {
      cancelAnimationFrame(rafId);
      clearTimeout(idleTimer);
      if (stageRO) stageRO.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', markRectDirty, true);
    };
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-dex-globe]').forEach(initGlobe);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();

  /* theme editor: sections are swapped in and out live */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-dex-globe]').forEach(function (el) {
      if (el.dexGlobeDestroy) el.dexGlobeDestroy();
    });
  });
})();
